import React, {
  useState,
  useLayoutEffect,
} from 'react';
import { useNavigate } from "react-router-dom";
import { on } from "devextreme/events";
import ListWidget from 'devextreme-react/list';
import List from "devextreme/ui/list";
import DataGrid from "devextreme/ui/data_grid";
import Button from 'devextreme-react/button';
import { Popup } from 'devextreme-react/popup';
import LoadPanel from 'devextreme-react/load-panel';
import ActionSheet from 'devextreme-react/action-sheet';

import dayjs from 'dayjs';
import {
  DataGrid as DataGridWidget,
  Column,
  Editing,
  SortByGroupSummaryInfo,
  Selection,
  Lookup,
  Button as GridButton,
  Toolbar,
  Item as ToolbarItem,
} from 'devextreme-react/data-grid';
import { appConst } from '../../app-const';
import { useStaffListApiService } from '../../services';
import {
  showError,
  showSuccess,
} from '../../helpers/ui';
import { useScreenSize } from '../../utils/media-query';
import { useGlobalState } from '../../contexts/global-state';
import { usePageTitle } from '../../contexts/page-title';
import { Badge, EmploymentStatuses } from '../../components';
import '../../components/component.scss';
import './staff-list.scss';

export default function ({ schoolId }) {
  const navigate = useNavigate();
  const {
    updateStaffListCurrentDateAsync,
    requestSchoolAssociationRemovalAsync,
    getStaffListBySchoolAsync,
    updateMemberEmploymentStatus,
  } = useStaffListApiService();
  const [showActionSheet, setShowActionSheet] = useState(false);
  const { isXSmall, isSmall } = useScreenSize();
  const showList = isXSmall || isSmall;
  const {
    setSelectedMembers,
    selectedMembers,
    selectedSchool,
    setSelectedSchool,
    managedSchools,
    setManagedSchools,
    defaultSuggestedSAStartAndEndDates,
    lookupData,
  } = useGlobalState();
  const { setTitle } = usePageTitle();
  const [showRemoveMemberPopup, setShowRemoveMemberPopup] = useState(false);
  const [showLoadingPanel, setShowLoadingPanel] = useState(false);
  const [employmentStatusEffectectiveDate, setEmploymentStatusEffectectiveDate] = useState();

  const actionSheetItems = [
    { text: 'Select All Staff', id: 1 },
    { text: 'Unselect All Staff', id: 2 },
    { text: 'Request Selected Staff To Review Their Records', id: 3 },
    { text: 'Remove Selected Staff From List', id: 4 },
    { text: 'Add Members To Join Staff List', id: 5 },
    { text: 'This Staff List Is Up To Date', id: 6, type: "default" },
    { text: 'Cancel', id: 7 },
  ];

  useLayoutEffect(() => {
    selectedSchool && setTitle(selectedSchool.name);
    window.addEventListener('resize', setHeight);
    setHeight();
    return () => window.removeEventListener('resize', setHeight);
  }, [
    selectedSchool,
    setTitle,
  ]);

  function renderRemoveMemberPopup() {
    return (
      <>
        {showRemoveMemberPopup && <Popup
          visible={showRemoveMemberPopup}
          height={(isXSmall || isSmall) ? "100%" : "50%"}
          width={(isXSmall || isSmall) ? "100%" : "50%"}
          title="Staff to remove..."
          showCloseButton={true}
          onHiding={() => setShowRemoveMemberPopup(false)}
        >
          <LoadPanel
            shadingColor="rgba(0,0,0,0.4)"
            visible={showLoadingPanel}
          />
          <DataGridWidget
            dataSource={selectedMembers}
            height="80%"
            columnHidingEnabled={true}
          >
            <Editing
              allowAdding={false}
              allowDeleting={true}
              allowUpdating={true}
            />
            <Column
              dataField="FullName"
              allowEditing={false}
            />
            <Column
              dataField="EndDate"
              dataType="date"
              format={appConst.dateDisplayFormat} />
          </DataGridWidget>
          <div style={{ display: 'flex', justifyContent: 'right', marginTop: '10px', gap: '10px' }}>
            <Button
              type='default'
              text="Process"
              onClick={async () => {
                setShowLoadingPanel(true);
                try {
                  await requestSchoolAssociationRemovalAsync(selectedSchool.schoolId,
                    selectedMembers.map(a => ({ Id: a.Id, EndDate: a.EndDate })));
                } catch (error) {
                  showError(error.message)
                  setShowLoadingPanel(false);
                  return;
                }

                const school = managedSchools.find(s => s.schoolId === parseInt(schoolId));
                school && (getStaffListBySchoolAsync(schoolId).then(r => {
                  school.staffList = r.data;
                  setSelectedSchool(school);
                  setShowRemoveMemberPopup(false);
                  setShowLoadingPanel(false);
                }));
              }}
            />
            <Button
              type="default"
              text="Cancel"
              onClick={() => { setShowRemoveMemberPopup(false) }}
            />
          </div>
        </Popup>}
      </>
    );
  }
  function renderHomeEmailBadge(hasHomeEmailAddress) {
    return hasHomeEmailAddress
      ? <Badge text="Present" color="green" />
      : <Badge text="Missing" color="red" />;
  }

  function renderMember(item) {
    return (
      <>
        <div className="item-container">
          <div className="full-name">{item.fullName}</div>
          <div className="memberId">
            {`MemberId=${item.memberId}`}
          </div>
        </div>
        <div style={{ textAlign: 'left' }}>Record last reviewed {(item.recordReviewedByMember !== null) ? dayjs(new Date(item.recordReviewedByMember)).format('MMM D, YYYY') : '(never)'}</div>
        <div className="home-email-container-list">
          <p>Home Email</p>
          <div className="home-email-badge-list">{renderHomeEmailBadge(item.hasHomeEmailAddress)}</div>
        </div>
        <div className="home-email-container-list">
        <EmploymentStatuses
          defaultValue={item.employmentStatusId}
          setEmploymentValues = {(v, d) => {
            item.employmentStatusId = v;
            item.employmentStatusEffectectiveDate = d;
          }}
        />
        <Button
          icon="save"
          id="saveBtn"
          hint="Save current employment status."
          onClick={async (e) => {
            e.event.stopPropagation();
            try {
              if (!item.hasOwnProperty("employmentStatusId") || !item.employmentStatusId) {
                showError("No employment status selected.")
                return;
              }

              const school = managedSchools.find(s => s.schoolId === parseInt(selectedSchool.schoolId));

              if (school) {
                setShowLoadingPanel(true);
                await updateMemberEmploymentStatus(selectedSchool.schoolId, item.memberId, item.employmentStatusId, item.employmentStatusEffectectiveDate);
                const response = await getStaffListBySchoolAsync(selectedSchool.schoolId);
                const updatedSchool = { ...school, staffList: response.data };
                setSelectedSchool(updatedSchool);
                setShowLoadingPanel(false);
              }

            } catch (error) {
              setShowLoadingPanel(false);
              showError(error.message);
          }
          }}
          />
        </div>
      </>)
  }

  function listHasSelectedItems(listInstance) {
    let selectedRows;
    if (listInstance.NAME === 'dxDataGrid') {
      selectedRows = listInstance.getSelectedRowKeys();
    } else {
      selectedRows = listInstance.option("selectedItemKeys")
    }
    if (selectedRows.length === 0) {
      showError('No members are selected.')
      return false;
    }
    return true;
  }

  function saveSelectedMembersToGlobalState(listInstance) {
    const selectedMembers = (listInstance.NAME === 'dxDataGrid') ? listInstance.getSelectedRowsData() : listInstance.option("selectedItems");
    setSelectedMembers(selectedMembers);
  }

  function pushHistory(path, listInstance) {
    saveSelectedMembersToGlobalState(listInstance);
    navigate(path);
  }

  async function updateStaffListToCurrentDateAsync() {
    try {
      const r = await updateStaffListCurrentDateAsync(selectedSchool.schoolId);
      if (r.data) {
        const tmpSelectedSchool = { ...selectedSchool };
        // ISO timestamp (like the API's), so the header can parse and format it.
        tmpSelectedSchool.staffListReviewedByCommRep = dayjs().format()
        setSelectedSchool(tmpSelectedSchool);

        const index = managedSchools.findIndex(s => s.schoolId === selectedSchool.schoolId);
        const tmpManagedSchools = [...managedSchools];
        tmpManagedSchools[index] = tmpSelectedSchool;
        setManagedSchools(tmpManagedSchools);

        showSuccess('Thank you for ensuring the staff list is up to date!');
      } else {
        showError('There was an error updating the staff list review date.')
      }
    }
    catch (error) {
      showError(error.message);
    }
  }

  function renderList() {
    return (
      <>
        <LoadPanel
          visible={showLoadingPanel}
        />
        <ListWidget
          id="memberList"
          dataSource={selectedSchool.staffList}
          selectAllMode='allPages'
          showSelectionControls={true}
          selectionMode='all'
          searchExpr="fullName"
          searchEnabled={true}
          searchMode={'contains'}
          itemRender={renderMember}
          scrollingEnabled={true}
          useNativeScrolling={true}
          pageLoadMode="scrollBottom"
          showScrollbar={'always'}
          onContentReady={(e) => {
            var itemContent = e.element.querySelectorAll(".dx-item-content");
            on(itemContent, "dxclick", function (event) {
              if (event.target.className === 'full-name') {
                event.stopImmediatePropagation();
                let i = event.delegateTarget.innerHTML.indexOf("MemberId=") + "MemberId=".length;
                let j = event.delegateTarget.innerHTML.indexOf("<", i);
                let memberId = event.delegateTarget.innerHTML.substring(i, j);
                navigate(`/school/${schoolId}/${memberId}`);
              }
            })
          }}
          onSelectionChanged={
            (e) => {
              const tmp = e.component.option("selectedItems");
              const ds = tmp && tmp.map(a => ({ Id: a.memberId, FullName: a.fullName, EndDate: defaultSuggestedSAStartAndEndDates.defaultEndDate }));
              setSelectedMembers(ds);
            }
          }
        />
        {renderRemoveMemberPopup()}
      </>
    );
  }

  function renderGrid() {
    return (
      <>
        <DataGridWidget
          id="memberGrid"
          keyExpr={["memberId", "schoolId"]}
          {...appConst.defaultGridOptions}
          dataSource={selectedSchool.staffList}
          onRowUpdated={async (e) => {
            try {

              const school = managedSchools.find(s => s.schoolId === parseInt(selectedSchool.schoolId));

              if (school) {
                e.component.beginCustomLoading();
                await updateMemberEmploymentStatus(selectedSchool.schoolId, e.data.memberId, e.data.employmentStatusId, employmentStatusEffectectiveDate);
                const response = await getStaffListBySchoolAsync(selectedSchool.schoolId);
                const updatedSchool = { ...school, staffList: response.data };
                setSelectedSchool(updatedSchool);
                e.component.endCustomLoading();
              }
   
            } catch (error) {
              e.component.endCustomLoading();
              showError(error.message);
            }
          }}
        >
          <Selection
            mode="multiple"
            showCheckBoxesMode='always'
            selectAllMode="allPages"
          />
          <Editing
            mode="row"
            allowAdding={false}
            allowDeleting={false}
            allowUpdating={true}
           
          />
          <SortByGroupSummaryInfo
            summaryItem="count"
            sortOrder="desc"
          />
          {/* Listing items replaces the default toolbar, so the search panel is
              re-declared. The list view (small screens) has no toolbar; its
              Action... button stays in the page header. */}
          <Toolbar>
            <ToolbarItem location="after">
              {renderActionButton()}
            </ToolbarItem>
            <ToolbarItem name="searchPanel" />
          </Toolbar>
          <Column dataField="preferredFirstName" allowEditing={false} />
          <Column dataField="firstName" allowEditing={false} />
          <Column dataField="lastName" allowEditing={false} />
          <Column dataField="workEmailAddress" allowEditing={false} />
          <Column dataField="hasHomeEmailAddress" caption="Home Email" allowEditing={false}
            editCellRender={(cell) => {
              if (cell.rowType === 'data') {
                return (<div className="home-email-badge-container">{renderHomeEmailBadge(cell.data.hasHomeEmailAddress)}</div>)
              }
            }}
          />
          <Column dataField="division" allowEditing={false} />
          <Column dataField="recordReviewedByMember"
            dataType="datetime"
            format={appConst.dateAndTimeDisplayFormat}
            allowEditing={false}
          />
          <Column
            allowEditing={true}
            dataField="employmentStatusId"
            caption="Employment Status"
            editCellRender={(cellInfo) => {
              return (
                <EmploymentStatuses
                  defaultValue={cellInfo.value}
                  setEmploymentValues={(v, d) => {
                    cellInfo.setValue(v);
                    setEmploymentStatusEffectectiveDate(d);
                  }}
                />
              );
            }}
          >
            <Lookup
              dataSource={lookupData.employmentStatuses}
              valueExpr="value"
              displayExpr="text"
            />
          </Column>
          <Column type="buttons">
            <GridButton name="edit" hint="Change employment status" />
            <GridButton name="save" hint="Save employment status" />
            <GridButton name="cancel" hint="Undo employment status" />
          </Column>
        </DataGridWidget>
        {renderRemoveMemberPopup()}
      </>
    )
  }

  // Opens the action sheet. In the grid's toolbar on wider screens; in the
  // page header in list view.
  function renderActionButton() {
    return (
      <Button
        id="openActionListButton"
        className="overflow-button"
        text="Action..."
        onClick={() => {
          setShowActionSheet(!showActionSheet);
        }}
      />
    );
  }

  function setHeight() {
    let resizeElement = document.getElementById("memberList");
    let resizeInstance;
    if (resizeElement) {
      resizeInstance = List.getInstance(resizeElement);
    } else {
      resizeElement = document.getElementById("memberGrid");
      if (resizeElement) {
        resizeInstance = DataGrid.getInstance(resizeElement);
      }
    }
    if (resizeInstance) {
      resizeInstance.option("height", window.innerHeight - resizeElement.getBoundingClientRect().y - 20);
    }
  }

  return ((selectedSchool && selectedSchool.staffList) ?
    <div className="staff-list-component">
      <div className="staff-list-header responsive-paddings">
        <div className="school-info-container">
          <div id="principalName">
            Principal: {selectedSchool && selectedSchool.principalName}
          </div>
          <div id="memberCount">
            Member Count: {selectedSchool && selectedSchool.staffList && selectedSchool.staffList.length}
          </div>
          <div id="lastReviewed">
            {selectedSchool?.staffListReviewedByCommRep
              ? `Last reviewed on ${dayjs(selectedSchool.staffListReviewedByCommRep).format('MMMM D, YYYY h:mm A')}`
              : 'Not yet reviewed'}
          </div>
        </div>
        {showList && (
          <div className="action-button-container">
            {renderActionButton()}
          </div>
        )}
      </div>
      <div className="list-container">
        {showList ? renderList() : renderGrid()}
      </div>
      <ActionSheet
        id="actionSheet"
        dataSource={actionSheetItems}
        visible={showActionSheet}
        showTitle={false}
        showCancelButton={false}
        onItemClick={
          async (e) => {
            setShowActionSheet(false);
            let action = e.itemData.id;
            let containerElement = showList ? document.getElementById("memberList") : document.getElementById("memberGrid");
            let containerInstance = (showList) ? List.getInstance(containerElement) : DataGrid.getInstance(containerElement);
            switch (action) {
              case 1:
                if (containerInstance.NAME === 'dxDataGrid') {
                  const allKeys = selectedSchool.staffList.map(({ memberId, schoolId }) => ({ memberId, schoolId }));
                  containerInstance.selectRows(allKeys, false);
                } else {
                  containerInstance.selectAll();
                }
                break;
              case 2:
                if (containerInstance.NAME === 'dxList') {
                  containerInstance.option("selectedItemKeys", []);
                }
                else {
                  containerInstance.deselectAll();
                }
                break;
              case 3:
                if (!listHasSelectedItems(containerInstance)) return;
                pushHistory('review-records-request', containerInstance);
                break;
              case 4:
                if (!listHasSelectedItems(containerInstance)) return;
                const tmp = (containerInstance.NAME === 'dxDataGrid') ? containerInstance.getSelectedRowsData() : containerInstance.option("selectedItems");
                const ds = tmp && tmp.map(a => ({ Id: a.memberId, FullName: a.fullName, EndDate: defaultSuggestedSAStartAndEndDates.defaultEndDate, SchoolAssociationId: a.schoolAssociationId }));
                setSelectedMembers(ds);
                setShowRemoveMemberPopup(true);
                break;
              case 5:
                pushHistory('add-staff-request', containerInstance);
                break;
              case 6:
                await updateStaffListToCurrentDateAsync();
                break;
              default:
                break;
            }
          }
        }
        onActionSheetCancelClick={
          (e) => {
            setShowActionSheet(false);
          }
        }
      />
    </div>
    : (<></>)
  );
}